import { Route, Routes } from "react-router-dom";
import {
  Activity,
  Briefcase,
  FileText,
  FolderOpen,
  Gauge,
  Inbox,
  LayoutGrid,
  PackageCheck,
  ReceiptText,
  Scale,
  ShieldCheck,
  Truck,
  SlidersHorizontal,
  UserCog,
  Users,
} from "lucide-react";
import { Coquille, type Entree } from "@/composants/coquille";
import { Chargement } from "@/composants/ui/etats";
import { PageAccueil } from "@/pages/accueil";
import { PageAffaire } from "@/pages/affaire";
import { PageAffaires } from "@/pages/affaires";
import { PageLot } from "@/pages/lot";
import { PageFiche } from "@/pages/fiche";
import { PageLots } from "@/pages/lots";
import { PageClient } from "@/pages/client";
import { PageClients } from "@/pages/clients";
import { PageCommande } from "@/pages/commande";
import { PageCommandeFournisseur } from "@/pages/commande-fournisseur";
import { PageCommandeNouvelle } from "@/pages/commande-nouvelle";
import { PageConnexion } from "@/pages/connexion";
import { PageDocumentsEntrants } from "@/pages/documents-entrants";
import { PageEcartsPrix } from "@/pages/ecarts-prix";
import { PageAvoirsEnCours } from "@/pages/avoirs-en-cours";
import { PageExploitation } from "@/pages/exploitation";
import { PageFournisseur } from "@/pages/fournisseur";
import { PageFournisseurNouveau } from "@/pages/fournisseur-nouveau";
import { PageFournisseurs } from "@/pages/fournisseurs";
import { PageIntrouvable } from "@/pages/introuvable";
import { PageRecherche } from "@/pages/recherche";
import { PageReferentiels } from "@/pages/referentiels";
import { PageModeleDocument } from "@/pages/modele-document";
import { PageModelesDocument } from "@/pages/modeles-document";
import { PageProfils } from "@/pages/profils";
import { PageSeuils } from "@/pages/seuils";
import { PageReceptionNouvelle } from "@/pages/reception-nouvelle";
import { PageReceptions } from "@/pages/receptions";
import { PageReceptionDuBl } from "@/pages/reception-du-bl";
import { PageComptes } from "@/pages/comptes";
import { useSession } from "@/session";

/* Le routeur est arrivé avec les écrans métier, comme le LOT 0 l'avait prévu :
   une adresse de Commande / Projet doit pouvoir se copier-coller et se partager. */
const ENTREES: Entree[] = [
  {
    vers: "/",
    libelle: "Commandes / Projets",
    icone: <FolderOpen className="h-4 w-4" aria-hidden />,
    intertitre: "Navigation principale",
    exact: true,
  },
  /* « Lots » se range sous « Commandes / Projets » ; « Affaires » reste à part
     (ticket #63, décision 107). */
  {
    vers: "/lots",
    libelle: "Lots",
    icone: <LayoutGrid className="h-4 w-4" aria-hidden />,
    intertitre: "Navigation principale",
    sous: true,
    parent: "/",
  },
  {
    vers: "/affaires",
    libelle: "Affaires",
    icone: <Briefcase className="h-4 w-4" aria-hidden />,
    intertitre: "Navigation principale",
  },
];

/* Le menu se range sous quatre intertitres (`INTERTITRES`, dans la coquille) :
   chaque entrée dit sous lequel elle tombe, et la coquille ne montre un
   intertitre que si une entrée visible le porte. */
const ENTREE_CLIENTS: Entree = {
  vers: "/clients",
  libelle: "Clients",
  icone: <Users className="h-4 w-4" aria-hidden />,
  intertitre: "Référentiels",
};

const ENTREE_EXPLOITATION: Entree = {
  vers: "/exploitation",
  libelle: "État du service",
  icone: <Activity className="h-4 w-4" aria-hidden />,
  intertitre: "Administration et pilotage",
};

/* L'administration des référentiels (ADR-0030) n'est proposée qu'à qui peut
   ADMINISTRER. Masquer n'est pas autoriser : le serveur refuse quoi qu'il en soit. */
const ENTREE_REFERENTIELS: Entree = {
  vers: "/administration/referentiels",
  libelle: "Référentiels",
  icone: <SlidersHorizontal className="h-4 w-4" aria-hidden />,
  intertitre: "Référentiels",
};

/* Ticket #106 (ADR-0060) : les modèles de document, que l'Administrateur compose,
   à côté des référentiels. */
const ENTREE_MODELES_DOCUMENT: Entree = {
  vers: "/administration/modeles-document",
  libelle: "Modèles de document",
  icone: <FileText className="h-4 w-4" aria-hidden />,
  intertitre: "Référentiels",
};

/* Les profils et les comptes (ADR-0035), à côté des référentiels, pour le même
   Administrateur. */
const ENTREES_ADMINISTRATION: Entree[] = [
  {
    vers: "/administration/profils",
    libelle: "Profils",
    icone: <ShieldCheck className="h-4 w-4" aria-hidden />,
    intertitre: "Administration et pilotage",
  },
  {
    vers: "/administration/comptes",
    libelle: "Comptes",
    icone: <UserCog className="h-4 w-4" aria-hidden />,
    intertitre: "Administration et pilotage",
  },
];

/* Les seuils d'alerte (LAB, 10/09 §05) et, depuis le ticket #50, la tolérance
   sur les frais fournisseur : pour qui gère les alertes, Responsable commercial
   compris, qui n'administre pas. */
const ENTREE_SEUILS: Entree = {
  vers: "/administration/seuils",
  libelle: "Seuils",
  icone: <Gauge className="h-4 w-4" aria-hidden />,
  intertitre: "Administration et pilotage",
};

/* Les fournisseurs (LOT 2) : un référentiel pour qui suit des commandes, pas
   pour le terrain, limité à ses affectations. Le serveur refuse quoi qu'il en soit. */
const ENTREE_FOURNISSEURS: Entree = {
  vers: "/fournisseurs",
  libelle: "Fournisseurs",
  icone: <Truck className="h-4 w-4" aria-hidden />,
  intertitre: "Référentiels",
};

/* La file des documents entrants (ADR-0040), pour qui peut la traiter. */
const ENTREE_DOCUMENTS_ENTRANTS: Entree = {
  vers: "/documents-entrants",
  libelle: "Documents entrants",
  icone: <Inbox className="h-4 w-4" aria-hidden />,
  intertitre: "Approvisionnements",
};

/* Ticket #39 : les écarts de prix à valider, pour qui tient la permission ; ticket
   #46 : à confirmer aussi, pour le chargé d'affaires qui voit les montants ;
   ticket #50 : les écarts de frais, pour qui tient leur permission. */
const ENTREE_ECARTS_PRIX: Entree = {
  vers: "/ecarts-prix",
  libelle: "Écarts de prix",
  icone: <Scale className="h-4 w-4" aria-hidden />,
  intertitre: "Approvisionnements",
};

/* Tickets #44 et #47 : les avoirs en cours — en négociation et attendus —, pour
   qui suit des commandes fournisseurs ; le serveur n'en montre que dans le
   périmètre de chacun. */
const ENTREE_AVOIRS_EN_COURS: Entree = {
  vers: "/avoirs-en-cours",
  libelle: "Avoirs en cours",
  icone: <ReceiptText className="h-4 w-4" aria-hidden />,
  intertitre: "Approvisionnements",
};

/* Ticket #22 : les commandes qui attendent une réception, pour qui la constate. */
const ENTREE_RECEPTIONS: Entree = {
  vers: "/receptions",
  libelle: "Réceptions",
  icone: <PackageCheck className="h-4 w-4" aria-hidden />,
  intertitre: "Approvisionnements",
};

export function App() {
  const session = useSession();

  if (session.statut === "chargement") return <Chargement />;
  // Pas de redirection vers une page de connexion : l'écran REMPLACE l'application
  // tant qu'il n'y a pas de session. L'adresse demandée reste donc dans la barre,
  // et la connexion rend l'écran qu'on voulait ouvrir.
  if (session.statut === "anonyme") return <PageConnexion />;

  const { permissions, lit_les_fournisseurs } = session.identite;
  /* L'ordre du tableau est celui de l'écran à l'intérieur d'un intertitre ;
     la coquille range ensuite par intertitre. */
  const entrees = [
    ...ENTREES,
    ...(lit_les_fournisseurs && permissions.includes("CONSTATER_RECEPTION")
      ? [ENTREE_RECEPTIONS]
      : []),
    ...(permissions.includes("TRAITER_DOCUMENTS_ENTRANTS") ? [ENTREE_DOCUMENTS_ENTRANTS] : []),
    // Un écart est un prix : sans « Consulter les montants », l'entrée n'a rien à montrer
    // (le serveur refuse la liste, et le garde-fou n° 5 interdit de valider sans voir).
    ...(permissions.includes("CONSULTER_MONTANTS") &&
    (permissions.includes("VALIDER_ECART_PRIX_FOURNISSEUR") ||
      permissions.includes("VALIDER_ECART_FRAIS_FOURNISSEUR") ||
      permissions.includes("VALIDER_FRAIS_COMMUNS") ||
      lit_les_fournisseurs)
      ? [ENTREE_ECARTS_PRIX]
      : []),
    ...(lit_les_fournisseurs ? [ENTREE_AVOIRS_EN_COURS] : []),
    ...(lit_les_fournisseurs ? [ENTREE_FOURNISSEURS] : []),
    ENTREE_CLIENTS,
    ...(permissions.includes("ADMINISTRER")
      ? [ENTREE_REFERENTIELS, ENTREE_MODELES_DOCUMENT]
      : []),
    ENTREE_EXPLOITATION,
    ...(permissions.includes("ADMINISTRER") ? ENTREES_ADMINISTRATION : []),
    ...(permissions.includes("REGLER_SEUILS_ALERTE") ? [ENTREE_SEUILS] : []),
  ];

  return (
    <Coquille entrees={entrees}>
      <Routes>
        <Route path="/" element={<PageAccueil />} />
        <Route path="/commandes/nouvelle" element={<PageCommandeNouvelle />} />
        <Route path="/commandes/:id" element={<PageCommande />} />
        <Route path="/affaires" element={<PageAffaires />} />
        <Route path="/affaires/:id" element={<PageAffaire />} />
        <Route path="/administration/referentiels" element={<PageReferentiels />} />
        <Route path="/administration/modeles-document" element={<PageModelesDocument />} />
        <Route path="/administration/modeles-document/:id" element={<PageModeleDocument />} />
        <Route path="/administration/profils" element={<PageProfils />} />
        <Route path="/administration/comptes" element={<PageComptes />} />
        <Route path="/administration/seuils" element={<PageSeuils />} />
        <Route path="/lots" element={<PageLots />} />
        <Route path="/lots/:id" element={<PageLot />} />
        <Route path="/fiches/:id" element={<PageFiche />} />
        <Route path="/clients" element={<PageClients />} />
        <Route path="/clients/:id" element={<PageClient />} />
        <Route path="/fournisseurs" element={<PageFournisseurs />} />
        <Route path="/fournisseurs/nouveau" element={<PageFournisseurNouveau />} />
        <Route path="/fournisseurs/:id" element={<PageFournisseur />} />
        <Route path="/commandes-fournisseurs/:id" element={<PageCommandeFournisseur />} />
        <Route path="/documents-entrants" element={<PageDocumentsEntrants />} />
        <Route path="/ecarts-prix" element={<PageEcartsPrix />} />
        <Route path="/avoirs-en-cours" element={<PageAvoirsEnCours />} />
        <Route path="/receptions" element={<PageReceptions />} />
        <Route path="/receptions/nouvelle" element={<PageReceptionNouvelle />} />
        <Route path="/receptions/bl/:id" element={<PageReceptionDuBl />} />
        <Route path="/recherche" element={<PageRecherche />} />
        <Route path="/exploitation" element={<PageExploitation />} />
        <Route path="*" element={<PageIntrouvable />} />
      </Routes>
    </Coquille>
  );
}
